import React, {
  createContext,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo
} from 'react';
import { isDevelopment } from 'lib/environment';
import isPrerenderCrawler from 'helpers/isPrerenderCrawler';

const ErrorBoundaryFallback = React.lazy(
  () => import('./ErrorBoundaryFallback')
);

const NetworkErrorFallback = React.lazy(() => import('./NetworkErrorFallback'));

const rollbarConfig = {
  accessToken: process.env.REACT_APP_ROLLBAR_ACCESS_TOKEN!,
  environment: process.env.REACT_APP_FEATURE_FLAGS!,
  code_version: process.env.REACT_APP_WEBAPP_VERSION,
  enabled: !isDevelopment && !isPrerenderCrawler()
};

const ErrorDisplay = ({ error }: { error: Error | null }) => {
  if (error) {
    if (error.name?.includes('Chunkload')) {
      return (
        <Suspense fallback={<></>}>
          <NetworkErrorFallback />
        </Suspense>
      );
    }
    return (
      <Suspense fallback={<></>}>
        <ErrorBoundaryFallback />
      </Suspense>
    );
  }
  return null;
};

type RollbarLike = {
  error: (...args: any[]) => unknown;
  configure: (...args: any[]) => unknown;
} | null;

export const RollbarContext = createContext<RollbarLike>(null);

type LazyRollbarClient = Exclude<RollbarLike, null> & {
  warmup: () => void;
};

const createLazyRollbarClient = (): LazyRollbarClient => {
  let rollbarClient: any = null;
  let rollbarPromise: Promise<any | null> | null = null;
  const pendingConfigs: any[] = [];

  const loadRollbar = async () => {
    if (rollbarClient) {
      return rollbarClient;
    }

    if (!rollbarPromise) {
      rollbarPromise = import('rollbar')
        .then(mod => {
          const RollbarConstructor = (mod.default ?? mod) as any;
          const client = new RollbarConstructor(rollbarConfig);

          pendingConfigs.forEach(config => {
            client.configure(config);
          });
          pendingConfigs.length = 0;

          rollbarClient = client;
          return client;
        })
        .catch(error => {
          console.warn('Failed to initialize rollbar', error);
          rollbarPromise = null;
          return null;
        });
    }

    return rollbarPromise;
  };

  return {
    warmup: () => {
      if (!rollbarConfig.enabled) {
        return;
      }

      void loadRollbar();
    },
    configure: config => {
      if (!rollbarConfig.enabled) {
        return;
      }

      if (rollbarClient) {
        rollbarClient.configure(config);
        return;
      }

      pendingConfigs.push(config);
    },
    error: (...args: any[]) => {
      if (!rollbarConfig.enabled) {
        return;
      }

      void loadRollbar().then(client => {
        client?.error(...args);
      });
    }
  };
};

class AppErrorBoundary extends React.Component<
  {
    children: React.ReactNode;
    onError?: (error: Error, info: React.ErrorInfo) => void;
  },
  { error: Error | null }
> {
  constructor(props: {
    children: React.ReactNode;
    onError?: (error: Error, info: React.ErrorInfo) => void;
  }) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    if (this.props.onError) {
      this.props.onError(error, info);
    }
  }

  render() {
    if (this.state.error) {
      return <ErrorDisplay error={this.state.error} />;
    }

    return this.props.children;
  }
}

const ErrorBoundary = ({ children }: { children: React.ReactNode }) => {
  const rollbar = useMemo<RollbarLike>(() => {
    if (!rollbarConfig.enabled) {
      return null;
    }

    return createLazyRollbarClient();
  }, []);

  useEffect(() => {
    if (!rollbar) {
      return;
    }

    const warmupRollbar = () => {
      (rollbar as LazyRollbarClient).warmup();
    };

    const idleHandle =
      typeof window.requestIdleCallback === 'function'
        ? window.requestIdleCallback(warmupRollbar, { timeout: 4000 })
        : window.setTimeout(warmupRollbar, 2000);

    return () => {
      if (typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleHandle as number);
      } else {
        clearTimeout(idleHandle as number);
      }
    };
  }, [rollbar]);

  const handleError = useCallback(
    (error: Error, info: React.ErrorInfo) => {
      if (rollbar) {
        rollbar.error(error, {
          componentStack: info.componentStack
        });
      }
    },
    [rollbar]
  );

  return (
    <RollbarContext.Provider value={rollbar}>
      <AppErrorBoundary onError={handleError}>{children}</AppErrorBoundary>
    </RollbarContext.Provider>
  );
};

export const useSharedRollbar = () => useContext(RollbarContext);

export default ErrorBoundary;
